import { createApp, ref, watch, type App, type Component } from 'vue'

import {
    BasketIcon,
    BookButton,
    BookForm,
    Calendar,
    CheckoutForm,
    DEFAULT_API_URL,
    installMancub,
    MainPrice,
    PriceList,
    requestOpenBasket,
    Review,
    setCurrency,
    useBasket,
    useCurrency,
    type BasketModalStep,
    type SuccessContentSource,
} from '@mancub/mancub-js-lib'
import '@mancub/mancub-js-lib/style.css'

declare const __MANCUB_WIDGET_VERSION__: string

const WIDGETS: Record<string, Component> = {
    'book-form': BookForm,
    'book-button': BookButton,
    'basket-icon': BasketIcon,
    'calendar': Calendar,
    'pricelist': PriceList,
    'main-price': MainPrice,
    'checkout': CheckoutForm,
    'review': Review,
}

const RESERVED_DATA_KEYS = new Set([
    'mancubWidget',
    'mancubType',
    'mancubMounted',
])

const MANCUB_PREFIX = 'mancub'

// data-mancub-foo → mancubFoo (HTML5 dataset rules) → foo (widget prop name).
// Lets consumers namespace prop attrs as data-mancub-* alongside the reserved
// data-mancub-{widget,type,mounted} ones; bare data-foo still works.
function dataKeyToPropName(key: string): string {
    if (key.startsWith(MANCUB_PREFIX) && key.length > MANCUB_PREFIX.length)
        return key.charAt(MANCUB_PREFIX.length).toLowerCase() + key.slice(MANCUB_PREFIX.length + 1)
    return key
}

// Widget props typed as string[] (e.g. BasketIcon/BasketModal/CheckoutForm's
// `currencies` and `preferredCountries`) — HTML data attributes can only carry
// strings, so a host sets e.g. data-mancub-currencies="EUR,USD,CZK" or
// data-mancub-preferred-countries="CZ,SK,DE" and we split it here. Without this
// the raw string would reach the component as-is and `for...of` iteration
// over it would yield individual characters instead of currency codes.
const ARRAY_PROP_NAMES = new Set(['currencies', 'preferredCountries'])

function coercePropValue(propName: string, value: string): unknown {
    if (ARRAY_PROP_NAMES.has(propName))
        return value.split(',').map(v => v.trim()).filter(Boolean)
    return value
}

// `document.currentScript` is non-null only while the IIFE is executing
// synchronously at script-tag load. Capture it now; it's null afterwards.
const loadingScript
    = document.currentScript instanceof HTMLScriptElement ? document.currentScript : null

const initialApiUrl
    = loadingScript?.dataset.mancubApiUrl
    ?? loadingScript?.dataset.apiUrl
    ?? DEFAULT_API_URL

const initialHost
    = loadingScript?.dataset.mancubHost
    ?? loadingScript?.dataset.host
    ?? ''

const initialCurrency
    = loadingScript?.dataset.mancubCurrency
    ?? loadingScript?.dataset.currency
    ?? null

const initialErrorDetails
    = (loadingScript?.dataset.mancubErrorDetails
        ?? loadingScript?.dataset.errorDetails) === 'true'

const initialSuccessContent
    = loadingScript?.dataset.mancubSuccessContent
    ?? loadingScript?.dataset.successContent
    ?? null

// Reactive so configure() calls take effect for any subsequent mount(); already
// mounted widgets keep the value they captured at mount time.
const globalApiUrl = ref<string>(initialApiUrl)
const globalHost = ref<string>(initialHost)
const globalErrorDetails = ref<boolean>(initialErrorDetails)
// Unlike the values above, widgets read this when the success dialog opens, so
// configure() reaches already mounted widgets too.
const globalSuccessContent = ref<SuccessContentSource>(initialSuccessContent)

// The browsing currency is the lib's one shared setting (not per mount):
// every mounted widget — booking form, price lists, basket — follows it live.
setCurrency(initialCurrency)

export interface ConfigureOptions {
    /** Engine API URL template; literal `{host}` is substituted with `host`. */
    apiUrl?: string
    /** Engine host — full hostname serving the tenant (e.g. `demo.w.mancub.io`). */
    host?: string
    /**
     * Currency the customer browses in (ISO code, e.g. `'EUR'`) — the one shared
     * setting all widgets (incl. already mounted ones) follow: price widgets show the
     * matching amount from a price's `amounts_by_currency` (including curated
     * exchange-bypass values), the basket and checkout total and order in it.
     */
    currency?: string | null
    /**
     * Show operator diagnostics on widget load errors — the "technical details"
     * disclosure (URL, status, body) and the exact missing commodity ident in
     * 404 messages. Off by default (visitors get only a friendly message);
     * enable for logged-in staff.
     */
    errorDetails?: boolean
    /**
     * Page-wide markup for the lower part of the "added to basket" dialog (e.g.
     * related products): a CSS selector of — or a reference to — a `<template>`
     * (its content is cloned) or any other element (its children are cloned),
     * copied in each time the dialog opens. A widget's own
     * `data-mancub-success-content` wins.
     */
    successContent?: SuccessContentSource
}

export function configure(options: ConfigureOptions): void {
    if (options.apiUrl !== undefined)
        globalApiUrl.value = options.apiUrl
    if (options.host !== undefined)
        globalHost.value = options.host
    if (options.currency !== undefined)
        setCurrency(options.currency)
    if (options.errorDetails !== undefined)
        globalErrorDetails.value = options.errorDetails
    if (options.successContent !== undefined)
        globalSuccessContent.value = options.successContent
}

export interface MountOptions {
    type?: keyof typeof WIDGETS | string
    props?: Record<string, unknown>
    /** Per-mount apiUrl override; wins over `configure()` and the script-tag default. */
    apiUrl?: string
    /** Per-mount host override; wins over `configure()` and the script-tag default. */
    host?: string
    /**
     * Per-mount `currency` prop: a display override on price widgets; on
     * `basket-icon` / `checkout` it sets the shared currency (see `configure`).
     */
    currency?: string | null
    /** Per-mount error-diagnostics toggle; wins over `configure()` and the script-tag default. */
    errorDetails?: boolean
    darkModeSelector?: string
    /**
     * Brand primary colour for the widget's PrimeVue palette. Accepts a hex
     * string (`'#0f766e'`), a CSS custom-property name (`'--my-primary'`) or a
     * `var()` reference. CSS variables are resolved from `document.documentElement`
     * at mount time, so override them in `:root` to retheme the widget.
     */
    primaryColor?: string
}

export function mount(target: HTMLElement, options: MountOptions = {}): App | null {
    const type = options.type ?? 'book-form'
    const Component = WIDGETS[type]
    if (!Component) {
        console.error(
            `[mancub] unknown widget type "${type}". Available: ${Object.keys(WIDGETS).join(', ')}`,
        )
        return null
    }

    const props = { ...options.props }
    if (options.currency != null)
        props.currency = options.currency
    const app = createApp(Component, props)
    installMancub(app, {
        // Per-mount apiUrl wins; otherwise widgets resolve via inject(MANCUB_API_URL).
        // Passing the ref keeps later configure() calls visible to widgets that
        // observe their injected value reactively.
        apiUrl: options.apiUrl ?? globalApiUrl,
        // Same resolution for the engine host (substituted into the apiUrl's
        // `{host}` placeholder); a per-element `data-mancub-host` still wins as a
        // widget prop over this injected default.
        host: options.host ?? globalHost,
        // Operator error diagnostics (technical-details disclosure); a host
        // enables it globally for staff via configure({ errorDetails: true }).
        errorDetails: options.errorDetails ?? globalErrorDetails,
        successContent: globalSuccessContent,
        darkModeSelector: options.darkModeSelector,
        primaryColor: options.primaryColor ?? target.dataset.mancubPrimaryColor,
    })
    app.mount(target)
    return app
}

// Mount-level options — consumed by installMancub before the widget Vue app
// boots, so they must not flow through to widget props.
const MOUNT_OPTION_DATA_KEYS = new Set([
    'mancubPrimaryColor',
    'mancubDarkModeSelector',
])

function readPropsFromDataset(el: HTMLElement): MountOptions {
    const props: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(el.dataset)) {
        if (RESERVED_DATA_KEYS.has(key) || MOUNT_OPTION_DATA_KEYS.has(key))
            continue
        const propName = dataKeyToPropName(key)
        props[propName] = coercePropValue(propName, value ?? '')
    }
    return {
        type: el.dataset.mancubType,
        props,
        primaryColor: el.dataset.mancubPrimaryColor,
        darkModeSelector: el.dataset.mancubDarkModeSelector,
    }
}

export function autoMount(): void {
    const elements = document.querySelectorAll<HTMLElement>('[data-mancub-widget]')
    elements.forEach((el) => {
        if (el.dataset.mancubMounted === 'true')
            return
        mount(el, readPropsFromDataset(el))
        el.dataset.mancubMounted = 'true'
    })
}

// Shared basket store (module-level reactive state, backed by localStorage).
// Lets host pages drive a custom basket button without mounting our own.
const basket = useBasket()

/**
 * Open the basket modal. Wire this to a host page's own basket button
 * (`onclick="MancubWidget.openBasket()"`) when a `basket-icon` widget is
 * mounted in `data-mancub-custom` mode — it renders only the modal and
 * listens for this signal.
 */
export function openBasket(step: BasketModalStep = 'items'): void {
    requestOpenBasket(step)
}

/** Current number of items in the basket. */
export function basketCount(): number {
    return basket.count.value
}

/**
 * Subscribe to basket count changes. Invokes `callback` immediately with the
 * current count, then on every change. Returns an unsubscribe function.
 */
export function onBasketChange(callback: (count: number) => void): () => void {
    callback(basket.count.value)
    return watch(basket.count, count => callback(count))
}

/**
 * Subscribe to browsing-currency changes — host `configure({ currency })` calls
 * and the customer's switch in the basket's currency dialog (the latter also
 * dispatches `mancub:currency-change` on `document`). Invokes `callback`
 * immediately with the current currency. Returns an unsubscribe function.
 */
export function onCurrencyChange(callback: (currency: string | null) => void): () => void {
    const { currency } = useCurrency()
    callback(currency.value)
    return watch(currency, value => callback(value))
}

const api = {
    version: __MANCUB_WIDGET_VERSION__,
    mount,
    autoMount,
    configure,
    openBasket,
    basketCount,
    onBasketChange,
    onCurrencyChange,
}

;(window as unknown as { MancubWidget: typeof api }).MancubWidget = api

if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', autoMount)
else
    autoMount()

export default api
